import _ from "lodash";
import { propertyToLowerCase } from "../globals/functions";
import { useEffect, useReducer } from "react";
import { Filter } from "../globals/types";

export function useItemFilter<T>(sortAttribute: string, sortDirection: "ascending" | "descending", items: T[], filters: Filter[]) {
    type State = {
        sortAttribute: string;
        sortDirection: "ascending" | "descending";
        filters: Filter[],
        items: T[];
        itemsOriginal: T[];
    }

    type Action = {
        type: "changeSort";
        sortAttribute: string;
    } | {
        type: "addFilter";
        filter: Filter;
    } | {
        type: "removeFilter";
        id: number;
    } | {
        type: "applyFilters";
    }

    function reducer(state: State, action: Action) {
        switch (action.type) {
            case "changeSort":
                if (state.sortAttribute === action.sortAttribute) {
                    return {
                        ...state,
                        items: state.items.slice().reverse(),
                        sortDirection: state.sortDirection === "ascending" ? "descending" : "ascending"
                    }
                }

                return {
                    ...state,
                    sortAttribute: action.sortAttribute,
                    sortDirection: 'ascending',
                    items: _.orderBy(state.items, [p => propertyToLowerCase(p, action.sortAttribute)], ["asc"]),
                }
            case "addFilter": {
                const newFilters = state.filters.concat([action.filter]);
                const sortDirection = state.sortDirection === "ascending" ? "asc" : "desc";
                const newItems = _.orderBy(
                    state.itemsOriginal.filter(i => newFilters.every(f => i[f.attribute] === f.value)),
                    [p => propertyToLowerCase(p, state.sortAttribute)],
                    [sortDirection]);

                return {
                    ...state,
                    filters: newFilters,
                    items: newItems
                }
            }
            case "removeFilter": {
                const newFilters = state.filters.filter(f => f.id !== action.id);
                const sortDirection = state.sortDirection === "ascending" ? "asc" : "desc";
                const newItems = _.orderBy(
                    state.itemsOriginal.filter(i => newFilters.every(f => i[f.attribute] === f.value)),
                    [p => propertyToLowerCase(p, state.sortAttribute)],
                    [sortDirection]);

                return {
                    ...state,
                    filters: newFilters,
                    items: newItems
                }
            }

            case "applyFilters": {
                const newFilters = state.filters;
                const sortDirection = state.sortDirection === "ascending" ? "asc" : "desc";
                const newItems = _.orderBy(
                    state.itemsOriginal.filter(i => newFilters.every(f => i[f.attribute] === f.value)),
                    [p => propertyToLowerCase(p, state.sortAttribute)],
                    [sortDirection]);

                return {
                    ...state,
                    filters: newFilters,
                    items: newItems
                }
            }
            default:
                throw new Error("Unknown action");
        }
    }

    const [state, dispatch] = useReducer(reducer, {
        sortAttribute: sortAttribute,
        sortDirection: sortDirection,
        filters: filters,
        items: items,
        itemsOriginal: items
    });

    useEffect(() => {
        dispatch({
            type: "applyFilters"
        });
    }, []);

    return [state, dispatch];
}